import { z } from 'zod';
import { supabase } from './supabase';

const EntitySchema = z.object({
  id: z.string().uuid(),
  entity_type: z.string(),
  canonical_name: z.string(),
  primary_language: z.string().nullable().optional(),
  country_code: z.string().nullable().optional(),
  status: z.string(),
});

const ReviewItemSchema = z.object({
  resolution: z.object({
    id: z.string().uuid(),
    raw_item_id: z.string().uuid(),
    entity_id: z.string().uuid().nullable(),
    score: z.coerce.number(),
    resolution_state: z.enum(['UNRESOLVED', 'AMBIGUOUS']),
    methods: z.array(z.unknown()),
    engine_version: z.string(),
    created_at: z.string(),
  }),
  rawItem: z.object({
    id: z.string().uuid(),
    source_identity_id: z.string().uuid(),
    platform_item_id: z.string().nullable(),
    canonical_url: z.string(),
    published_at: z.string().nullable(),
    first_seen_at: z.string(),
    raw_title: z.string().nullable(),
    raw_text: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()),
  }).nullable(),
  source: z.object({
    name: z.string(),
    authorityTier: z.number(),
    sourceRole: z.string().nullable(),
    platform: z.string(),
    handle: z.string().nullable(),
    canonicalUrl: z.string(),
    connectorType: z.string(),
    accessMode: z.string(),
  }).nullable(),
  resolvedEntity: EntitySchema.nullable(),
  override: z.object({
    id: z.string().uuid(),
    raw_item_id: z.string().uuid(),
    entity_id: z.string().uuid(),
    active: z.boolean(),
    reason: z.string(),
    created_by: z.string().uuid().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    entity: EntitySchema.nullable(),
  }).nullable(),
});

const BootstrapSchema = z.object({
  generatedAt: z.string(),
  items: z.array(ReviewItemSchema),
  eventTypes: z.array(z.object({
    code: z.string(),
    family: z.string(),
    default_importance: z.string(),
    taxonomy_version: z.number(),
  })),
  audit: z.array(z.object({
    id: z.string().uuid(),
    actor_type: z.string(),
    actor_id: z.string().uuid().nullable(),
    action_type: z.string(),
    target_type: z.string(),
    target_id: z.string().uuid().nullable(),
    before_json: z.unknown().nullable(),
    after_json: z.unknown().nullable(),
    reason: z.string().nullable(),
    created_at: z.string(),
  })),
});


const OfficialArticleReviewQueueSchema = z.object({
  generatedAt: z.string(),
  items: z.array(z.object({
    rawItem: z.object({
      id: z.string().uuid(),
      source_identity_id: z.string().uuid(),
      raw_title: z.string().nullable(),
      canonical_url: z.string(),
      first_seen_at: z.string(),
      published_at: z.string().nullable(),
    }),
    source: z.object({
      name: z.string(),
      authorityTier: z.coerce.number(),
      role: z.string(),
    }),
    resolution: z.object({
      raw_item_id: z.string().uuid(),
      resolution_state: z.enum(['UNRESOLVED','AMBIGUOUS']),
      score: z.coerce.number(),
      methods: z.array(z.unknown()),
      created_at: z.string(),
    }),
    typedSuggestion: z.object({
      id: z.string().uuid(),
      proposed_name: z.string(),
      proposed_entity_type: z.enum(['MOVIE','SERIES','SEASON']),
      status: z.string(),
      evidence_count: z.coerce.number(),
      first_party_source_count: z.coerce.number(),
      source_count: z.coerce.number(),
      confidence: z.coerce.number(),
      primary_language: z.string().nullable(),
      country_code: z.string().nullable(),
      metadata: z.record(z.string(), z.unknown()),
      producerEvidenceLeads: z.array(z.object({
        id:z.string().uuid(),
        candidate_id:z.string().uuid(),
        source_url:z.string(),
        claimed_owner_name:z.string(),
        evidence_note:z.string(),
        status:z.literal('NEEDS_PRIMARY_SOURCE_REVIEW'),
        created_at:z.string(),
        counts_toward_approval:z.literal(false),
      })),
    }).nullable(),
    reviewStatus: z.enum(['TYPED_CANDIDATE_REVIEW','MANUAL_TITLE_REVIEW']),
    needsHumanReview: z.literal(true),
  })),
});
const CorrectionImpactSchema = z.object({
  generatedAt:z.string(),
  referenceScanComplete:z.boolean(),
  expectedType:z.enum(['MOVIE','SERIES']),
  source:z.object({
    id:z.string().uuid(),canonical_name:z.string(),entity_type:z.enum(['MOVIE','SERIES']),
    status:z.string(),slug:z.string(),
    primary_language:z.string().nullable(),country_code:z.string().nullable(),
  }),
  target:z.object({
    id:z.string().uuid(),canonical_name:z.string(),entity_type:z.enum(['MOVIE','SERIES']),
    status:z.string(),slug:z.string(),
    primary_language:z.string().nullable(),country_code:z.string().nullable(),
  }).nullable(),
  sourceImpact:z.lazy(()=>CorrectionImpactDetailsSchema),
  targetImpact:z.lazy(()=>CorrectionImpactDetailsSchema).nullable(),
  risk:z.object({
    canAutoApply:z.literal(false),
    requiresOperatorReview:z.literal(true),
    classificationMismatch:z.boolean(),
    targetTypeMismatch:z.boolean(),
    releaseCollisions:z.array(z.object({
      sourceReleaseId:z.string().uuid(),
      targetReleaseId:z.string().uuid(),
      providerId:z.string().uuid(),
      territory:z.string().nullable(),
    })),
    warnings:z.array(z.string()),
  }),
});
const CorrectionImpactDetailsSchema=z.object({
  activeEvents:z.number(),historicalEvents:z.number(),
  ottReleaseRows:z.number(),promotedCandidates:z.number(),
  aliases:z.array(z.string()),claims:z.number(),activeFollowers:z.number(),
  relationshipProposals:z.number(),resolutionLinks:z.number(),
  alertDeliveries:z.number(),sentAlerts:z.number(),
  releaseRows:z.array(z.object({
    id:z.string().uuid(),providerId:z.string().uuid(),territory:z.string().nullable(),
    releaseType:z.string().nullable(),date:z.string().nullable(),precision:z.string(),
    state:z.string(),evidenceStatus:z.string(),
  })),
  eventSamples:z.array(z.object({
    id:z.string().uuid(),type:z.string(),status:z.string(),headline:z.string().nullable(),
  })),
});
export type CorrectionImpactPreview=z.infer<typeof CorrectionImpactSchema>;
export function fetchCorrectionImpactPreview(
  sourceEntityId:string,targetEntityId:string|null,expectedType:'MOVIE'|'SERIES',
){
  return invoke({action:'catalogueCorrectionImpactPreview',sourceEntityId,targetEntityId,expectedType},CorrectionImpactSchema);
}

const ActiveCatalogueQualityAuditSchema = z.object({
  generatedAt:z.string(),
  scannedActiveEntities:z.coerce.number(),
  scanComplete:z.boolean(),
  totalFlagged:z.coerce.number(),
  eventCountsComplete:z.boolean(),
  evidenceLinksComplete:z.boolean(),
  items:z.array(z.object({
    id:z.string().uuid(),
    entity_type:z.enum(['MOVIE','SERIES']),
    canonical_name:z.string(),
    status:z.literal('ACTIVE'),
    primary_language:z.string().nullable(),
    country_code:z.string().nullable(),
    created_at:z.string(),
    warnings:z.array(z.string()),
    activeEventCount:z.coerce.number(),
    eventSamples:z.array(z.object({
      id:z.string().uuid(),
      eventType:z.string(),
      headline:z.string().nullable(),
      detectedAt:z.string(),
      occurredAt:z.string().nullable(),
    })),
    sourceEvidence:z.array(z.object({
      rawItemId:z.string().uuid(),
      rawTitle:z.string().nullable(),
      sourceUrl:z.string().nullable(),
      publishedAt:z.string().nullable(),
      platform:z.string(),
      sourceName:z.string(),
    })),
    linkedPromotedCandidates:z.array(z.object({
      id:z.string().uuid(),
      proposedName:z.string(),
      status:z.string(),
      confidence:z.coerce.number(),
      reviewReason:z.string().nullable(),
    })),
  })),
});
export type ActiveCatalogueQualityAudit = z.infer<typeof ActiveCatalogueQualityAuditSchema>;
export function fetchActiveCatalogueQualityAudit(limit=50) {
  return invoke({action:'activeCatalogueQualityAudit',limit},ActiveCatalogueQualityAuditSchema);
}

const PendingCatalogueQueueSchema = z.object({
  generatedAt: z.string(),
  items: z.array(z.object({
    id: z.string().uuid(),
    proposed_name: z.string(),
    proposed_entity_type: z.enum(['MOVIE','SERIES']),
    status: z.literal('PENDING'),
    confidence: z.coerce.number(),
    evidence_count: z.coerce.number(),
    source_count: z.coerce.number(),
    first_party_source_count: z.coerce.number(),
    independent_owner_count: z.coerce.number(),
    owner_count_complete: z.boolean(),
    primary_language: z.string().nullable(),
    country_code: z.string().nullable(),
    metadata: z.record(z.string(),z.unknown()),
    last_seen_at: z.string(),
    evidence: z.array(z.object({
      id: z.string().uuid(),
      rawTitle: z.string().nullable(),
      sourceUrl: z.string().nullable(),
      publishedAt: z.string().nullable(),
      itemType: z.string().nullable(),
      sourceName: z.string(),
      sourceRole: z.string().nullable(),
      platform: z.string(),
      isFirstParty: z.boolean(),
      matchMethod: z.string(),
    })),
  })),
});
export type PendingCatalogueReviewQueue = z.infer<typeof PendingCatalogueQueueSchema>;
export function fetchPendingCatalogueReviewQueue(limit = 50) {
  return invoke({ action:'pendingCatalogueReviewQueue',limit }, PendingCatalogueQueueSchema);
}

export type OfficialArticleReviewItem = z.infer<typeof OfficialArticleReviewQueueSchema>;

export function fetchOfficialArticleReviewQueue(limit = 50) {
  return invoke({action:'officialArticleReviewQueue',limit}, OfficialArticleReviewQueueSchema);
}

const SearchSchema = z.object({ items: z.array(EntitySchema) });
const ActionSchema = z.object({ ok: z.literal(true), result: z.record(z.string(), z.unknown()) });

export type ReviewBootstrap = z.infer<typeof BootstrapSchema>;
export type ReviewItem = z.infer<typeof ReviewItemSchema>;
export type ReviewEntity = z.infer<typeof EntitySchema>;

async function invoke<T>(body: Record<string, unknown>, schema: z.ZodType<T>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('cinerelay-review-api', { body });
  if (error) throw error;
  return schema.parse(data);
}

export function fetchReviewBootstrap(limit = 100) {
  return invoke({ action: 'bootstrap', limit }, BootstrapSchema);
}

export function searchReviewEntities(query: string) {
  return invoke({ action: 'entitySearch', query }, SearchSchema);
}

export function resolveReviewItem(input: {
  rawItemId: string;
  reason: string;
  entityId?: string;
  newEntityName?: string;
  newEntityType?: 'MOVIE' | 'SERIES' | 'SEASON';
  primaryLanguage?: string;
  countryCode?: string;
}) {
  return invoke({ action: 'resolveRawItem', ...input }, ActionSchema);
}

export function clearReviewOverride(rawItemId: string, reason: string) {
  return invoke({ action: 'clearResolutionOverride', rawItemId, reason }, ActionSchema);
}

export function suppressEvent(eventId: string, reason: string) {
  return invoke({ action: 'suppressEvent', eventId, reason }, ActionSchema);
}

export function reclassifyEvent(eventId: string, eventType: string, reason: string) {
  return invoke({ action: 'reclassifyEvent', eventId, eventType, reason }, ActionSchema);
}

export function mergeEvents(fromEventId: string, intoEventId: string, reason: string) {
  return invoke({ action: 'mergeEvents', fromEventId, intoEventId, reason }, ActionSchema);
}

export function submitProducerEvidenceLead(input:{candidateId:string;sourceUrl:string;claimedOwnerName:string;evidenceNote:string}){
  return invoke({action:'submitProducerEvidenceLead',...input},ActionSchema);
}
